import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Select,
  Form,
  Input,
  Button,
  Upload,
  Table,
  Space,
  Popconfirm,
  message,
  Empty,
  Row,
  Col,
  Radio,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import SchoolApi, { fileUrl } from "./api";

const OWNER_LABEL = { school: "Okul", grade: "Sınıf", branch: "Şube" };

const SchoolFiles = () => {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(null);
  const [grades, setGrades] = useState([]);
  const [branches, setBranches] = useState([]);

  const [ownerType, setOwnerType] = useState("branch");
  const [ownerId, setOwnerId] = useState(null);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileObj, setFileObj] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    SchoolApi.listSchools()
      .then((d) => setSchools((d || []).filter((s) => s.status !== 3)))
      .catch(() => message.error("Okullar getirilemedi"));
  }, []);

  const onSchoolChange = (sid) => {
    setSchoolId(sid);
    setOwnerId(null);
    setFiles([]);
    Promise.all([SchoolApi.listGrades(sid), SchoolApi.listAllBranches(sid)])
      .then(([gr, br]) => {
        setGrades(gr || []);
        setBranches(br || []);
        if (ownerType === "school") setOwnerId(sid);
      })
      .catch(() => message.error("Liste getirilemedi"));
  };

  const onOwnerTypeChange = (e) => {
    const t = e.target.value;
    setOwnerType(t);
    setFiles([]);
    setOwnerId(t === "school" && schoolId ? schoolId : null);
  };

  const ownerOptions = () => {
    if (ownerType === "school") return schools.map((s) => ({ value: s.id, label: s.name }));
    if (ownerType === "grade") return grades.map((g) => ({ value: g.id, label: g.name }));
    return branches.map((b) => ({ value: b.branch_id, label: `${b.grade_name} / ${b.branch_name}` }));
  };

  // Yükleme/silme sonrası anlık istek bazen (proxy/keepalive) düşebiliyor;
  // bir kaç kez sessizce yeniden dener, gerçekten olmazsa uyarır.
  const fetchFiles = useCallback((type, id, { retry = 1, silent = false } = {}) => {
    if (!type || !id) return;
    setLoading(true);
    SchoolApi.listFiles(type, id)
      .then((d) => setFiles(d || []))
      .catch(() => {
        if (retry > 0) {
          setTimeout(() => fetchFiles(type, id, { retry: retry - 1, silent }), 600);
        } else if (!silent) {
          message.error("Dosyalar getirilemedi");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchFiles(ownerType, ownerId);
  }, [ownerType, ownerId, fetchFiles]);

  const doUpload = (values) => {
    if (!fileObj) return message.warning("Bir dosya seçin");
    if (!ownerId) return message.warning("Önce hedef (okul/sınıf/şube) seçin");
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("title", values.title?.trim() || fileObj.name);
    fd.append("description", values.description?.trim() || "");
    fd.append("owner_type", ownerType);
    fd.append("owner_id", ownerId);

    const t = ownerType;
    const id = ownerId;
    const desc = values.description?.trim() || null;

    setUploading(true);
    SchoolApi.uploadFile(fd)
      .then((created) => {
        message.success("Dosya yüklendi");
        form.resetFields();
        setFileObj(null);
        // Dönen kaydı listeye anında ekle (refetch'e bağımlı olma).
        if (created && created.id) {
          setFiles((prev) => [
            {
              ...created,
              description: desc,
              created_at: new Date().toISOString(),
              uploaded_by_name: null,
            },
            ...prev.filter((f) => f.id !== created.id),
          ]);
        }
        // Arka planda sunucuyla uzlaştır; düşerse sessiz (kayıt zaten listede).
        fetchFiles(t, id, { retry: 3, silent: true });
      })
      .catch((e) => message.error(e?.response?.data?.message || "Yükleme başarısız"))
      .finally(() => setUploading(false));
  };

  const remove = (row) => {
    SchoolApi.deleteFile(row.id)
      .then(() => {
        message.success("Dosya silindi");
        setFiles((prev) => prev.filter((f) => f.id !== row.id));
        fetchFiles(ownerType, ownerId, { retry: 2, silent: true });
      })
      .catch((e) => message.error(e?.response?.data?.message || "Silinemedi"));
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 70 },
    {
      title: "Başlık",
      dataIndex: "title",
      render: (v, r) => (
        <a href={fileUrl(r.file_path)} target="_blank" rel="noreferrer">
          {v}
        </a>
      ),
    },
    { title: "Tür", dataIndex: "file_type", width: 90, render: (v) => (v ? v.toUpperCase() : "-") },
    { title: "Açıklama", dataIndex: "description", render: (v) => v || "-" },
    { title: "Yükleyen", dataIndex: "uploaded_by_name", width: 160, render: (v) => v || "-" },
    {
      title: "Tarih",
      dataIndex: "created_at",
      width: 160,
      render: (v) => (v ? moment(v).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "İşlem",
      width: 180,
      render: (_, r) => (
        <Space>
          <Button size="small" href={fileUrl(r.file_path)} target="_blank">
            İndir
          </Button>
          <Popconfirm title="Dosya silinsin mi?" onConfirm={() => remove(r)} okText="Evet" cancelText="Hayır">
            <Button size="small" danger>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid p-3">
      <h1 className="h3 mb-3">
        <strong>Dosya Yönetimi</strong>
      </h1>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <label>Okul</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Okul seçin"
              optionFilterProp="label"
              value={schoolId}
              onChange={onSchoolChange}
              options={schools.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Col>
          <Col xs={24} md={8}>
            <label>Hedef Türü</label>
            <div>
              <Radio.Group value={ownerType} onChange={onOwnerTypeChange} optionType="button" buttonStyle="solid">
                <Radio.Button value="school">Okul</Radio.Button>
                <Radio.Button value="grade">Sınıf</Radio.Button>
                <Radio.Button value="branch">Şube</Radio.Button>
              </Radio.Group>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <label>{OWNER_LABEL[ownerType]} seçin</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder={schoolId ? `${OWNER_LABEL[ownerType]} seçin` : "Önce okul seçin"}
              optionFilterProp="label"
              disabled={!schoolId}
              value={ownerId}
              onChange={setOwnerId}
              options={ownerOptions()}
            />
          </Col>
        </Row>
      </Card>

      {!ownerId ? (
        <Empty description="Devam etmek için hedef (okul/sınıf/şube) seçin" />
      ) : (
        <>
          <Card title="Dosya Yükle" size="small" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" onFinish={doUpload}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Başlık" name="title">
                    <Input placeholder="Boş bırakılırsa dosya adı kullanılır" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={10}>
                  <Form.Item label="Açıklama" name="description">
                    <Input placeholder="Opsiyonel" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="Dosya">
                    <Upload
                      beforeUpload={(f) => {
                        setFileObj(f);
                        return false;
                      }}
                      maxCount={1}
                      fileList={fileObj ? [fileObj] : []}
                      onRemove={() => setFileObj(null)}
                    >
                      <Button icon={<UploadOutlined />}>Dosya seç</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Button type="primary" htmlType="submit" loading={uploading}>
                Yükle
              </Button>
            </Form>
          </Card>

          <Card title={`Yüklü Dosyalar (${files.length})`}>
            <Table rowKey="id" size="small" loading={loading} dataSource={files} columns={columns} pagination={{ pageSize: 20 }} />
          </Card>
        </>
      )}
    </div>
  );
};

export default SchoolFiles;
